import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  OrderUpdateChannelInput,
  OrderUpdateCustomerInfoInput,
  OrderUpdateDiscountInput,
  OrderUpdateItemsInput,
  OrderUpdatePaymentMethodInput,
  OrderUpdateStatusInput,
  OrderUpdateTrackingInput,
} from "@/modules/orders/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import type { OrderCreateAttachmentInput } from "./client";
import { ordersApi } from "./client";

export type { OrderCreateAttachmentInput } from "./client";

export const orderKeys = {
  all: ["orders"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["orders", "list", q] as const,
  detail: (id: string) => ["orders", "detail", id] as const,
  summary: () => ["orders", "summary"] as const,
};

export function useOrdersQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: orderKeys.list(q),
    queryFn: () => ordersApi.list(q),
  });
}

export function useOrderQuery(id: string) {
  return useQuery({
    queryKey: orderKeys.detail(id),
    queryFn: () => ordersApi.get(id),
    enabled: !!id,
  });
}

export function useOrderSummaryQuery() {
  return useQuery({
    queryKey: orderKeys.summary(),
    queryFn: () => ordersApi.getSummary(),
  });
}

export function useUpdateOrderStatus(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderUpdateStatusInput) =>
      ordersApi.updateStatus(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      toast.success("ປ່ຽນສະຖານະສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ປ່ຽນສະຖານະລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useAddOrderAttachment(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderCreateAttachmentInput) =>
      ordersApi.createAttachment(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
    },
    onError: (error: unknown) => {
      toast.error("ອັບໂຫຼດລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useDeleteOrderAttachment(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) =>
      ordersApi.deleteAttachment(orderId, attachmentId),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("ລຶບຮູບສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ລຶບລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useUpdateOrderCustomerInfo(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderUpdateCustomerInfoInput) =>
      ordersApi.updateCustomerInfo(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("ບັນທຶກຂໍ້ມູນລູກຄ້າສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກຂໍ້ມູນລູກຄ້າລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useUpdateOrderDiscount(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderUpdateDiscountInput) =>
      ordersApi.updateDiscount(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      toast.success("ບັນທຶກສ່ວນຫຼຸດສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກສ່ວນຫຼຸດລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useUpdateOrderItems(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderUpdateItemsInput) =>
      ordersApi.updateItems(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      toast.success("ບັນທຶກລາຍການສິນຄ້າສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກລາຍການສິນຄ້າລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useUpdateOrderPaymentMethod(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderUpdatePaymentMethodInput) =>
      ordersApi.updatePaymentMethod(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("ບັນທຶກວິທີຊຳລະສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກວິທີຊຳລະລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useUpdateOrderChannel(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderUpdateChannelInput) =>
      ordersApi.updateChannel(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("ບັນທຶກຊ່ອງທາງຂາຍສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກຊ່ອງທາງຂາຍລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useUpdateOrderTracking(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: OrderUpdateTrackingInput) =>
      ordersApi.updateTracking(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      toast.success("ບັນທຶກເລກຕິດຕາມສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກເລກຕິດຕາມລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useCreateHalPreOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ordersApi.createHalPreOrder(orderId),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      toast.success("ສ້າງ HAL shipment ສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ສ້າງ HAL shipment ລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useUpdateHalPreOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ordersApi.updateHalPreOrder(orderId),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("ອັບເດດ HAL pre-order ສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ອັບເດດ HAL pre-order ລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useCancelHalPreOrder(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ordersApi.cancelHalPreOrder(orderId),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      toast.success("ຍົກເລີກ HAL pre-order ສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ຍົກເລີກ HAL pre-order ລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useRefreshHalTracking(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => ordersApi.refreshHalTracking(orderId),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      toast.success("ອັບເດດ HAL tracking ສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ອັບເດດ HAL tracking ລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function useAddOrderReturnLines(orderId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      lines: {
        orderItemId: string;
        quantityReturned: number;
        reason?: string | null;
      }[];
    }) => ordersApi.createReturnLines(orderId, input),
    onSuccess: (data) => {
      qc.setQueryData(orderKeys.detail(orderId), data);
      qc.invalidateQueries({ queryKey: orderKeys.all });
      toast.success("ບັນທຶກຮັບຄືນສຳເລັດ");
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກຮັບຄືນລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}
